import {
  MEDICAL_CERTIFICATE_CONFIG,
  type MedicalCertificateContext,
  type MedicalCertificateData,
  type MedicalCertificateExtraction,
} from "@asap/shared/document/document-types/medical-certificate";
import { MedicalCertificateSchemas } from "@asap/shared/schemas/document/document-types/medical-certificate";
import { formatFirstName, formatLastName } from "@asap/shared/utils/string";
import { DocumentTypeHandler } from "../handler";

/**
 * Medical Certificate Handler (Certificat médical / Avis d'aptitude)
 */
export const MedicalCertificateHandler = new DocumentTypeHandler<
  MedicalCertificateExtraction,
  MedicalCertificateData,
  MedicalCertificateContext
>({
  // Identity
  documentType: MEDICAL_CERTIFICATE_CONFIG.documentType,
  documentName: MEDICAL_CERTIFICATE_CONFIG.documentName,

  // Constraints
  maxFiles: MEDICAL_CERTIFICATE_CONFIG.maxFiles,
  acceptedMimeTypes: MEDICAL_CERTIFICATE_CONFIG.acceptedMimeTypes,
  requiredFields: MEDICAL_CERTIFICATE_CONFIG.requiredFields,

  // API endpoints
  endpoints: {
    extract: "documents/medical-certificate/extract",
    warn: "documents/medical-certificate/warn",
    verify: "documents/medical-certificate/verify",
    store: "documents/medical-certificate/store",
  },

  // Response schemas for runtime validation
  schemas: {
    extractResponse: MedicalCertificateSchemas.extractResponse,
    warnResponse: MedicalCertificateSchemas.warnResponse,
    validateResponse: MedicalCertificateSchemas.validateResponse,
    storeResponse: MedicalCertificateSchemas.storeResponse,
  },

  // Toast messages
  toastMessages: {
    success: "Le certificat médical a bien été validé.",
  },

  // Form fields
  formFields: [
    {
      key: "certificate_type",
      label: MEDICAL_CERTIFICATE_CONFIG.fieldLabels.certificate_type,
      type: "select",
      options: [
        { value: "SIR", label: "SIR — Suivi individuel renforcé" },
        { value: "SIA", label: "SIA — Suivi individuel adapté" },
        { value: "VIPI", label: "VIPI — Visite d'information et de prévention" },
      ],
      isSearchable: false,
      colSpan: 2,
    },
    {
      key: "aptitude_opinion_date",
      label: MEDICAL_CERTIFICATE_CONFIG.fieldLabels.aptitude_opinion_date,
      type: "date",
      colSpan: 1,
    },
    {
      key: "next_visit_date",
      label: MEDICAL_CERTIFICATE_CONFIG.fieldLabels.next_visit_date,
      type: "date",
      colSpan: 1,
    },
  ],

  // Transform OCR extraction to form data
  transformExtraction: (extraction: MedicalCertificateExtraction): Partial<MedicalCertificateData> => {
    if (!extraction.isValidDocumentType) {
      throw new Error(
        "Le fichier ajouté ne semble pas correspondre à un certificat médical. Vérifie que ce soit bien le cas."
      );
    }

    const hasAnyExtractedField =
      extraction.first_name ||
      extraction.last_name ||
      extraction.certificate_type ||
      extraction.aptitude_opinion_date ||
      extraction.next_visit_date;

    if (!hasAnyExtractedField) {
      throw new Error("Le document n'a pas pu être lu. Merci de fournir un certificat médical lisible.");
    }

    const result: Partial<MedicalCertificateData> = {};

    // first_name/last_name are extracted for the talent-name coherence warning only —
    // intentionally not exposed as form fields (nothing here should let a user edit them).
    if (extraction.first_name) result.first_name = formatFirstName(extraction.first_name);
    if (extraction.last_name) result.last_name = formatLastName(extraction.last_name);
    if (extraction.certificate_type) result.certificate_type = extraction.certificate_type;
    if (extraction.aptitude_opinion_date) result.aptitude_opinion_date = extraction.aptitude_opinion_date;
    if (extraction.next_visit_date) result.next_visit_date = extraction.next_visit_date;

    return result;
  },
});
